import { useEffect, useState } from "react";

const BLOCKED_COUNTRIES = ["IN", "BD", "RU", "ID"];
const GEO_CACHE_KEY = "geo_block_status";

/**
 * Optimized geo-blocking hook that doesn't block initial render
 * - Uses cached value immediately if available
 * - Shows content by default (fail-open)
 * - Performs geo check in background without blocking paint
 */
export function useGeoBlock() {
    // Check for cached value synchronously to avoid flash
    const getCachedValue = () => {
        if (typeof window === "undefined") return { loading: false, blocked: false };
        const cached = sessionStorage.getItem(GEO_CACHE_KEY);
        if (cached !== null) {
            return { loading: false, blocked: cached === "true" };
        }
        return { loading: true, blocked: false };
    };

    const initialState = getCachedValue();
    const [loading, setLoading] = useState(initialState.loading);
    const [isBlocked, setIsBlocked] = useState(initialState.blocked);

    useEffect(() => {
        // If we already have a cached value, no need to fetch
        if (!initialState.loading) {
            return;
        }

        // Use requestIdleCallback to defer geo check until browser is idle
        // This prevents blocking the main thread during initial page load
        const checkGeo = async () => {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

                const res = await fetch("https://ipwho.is/", {
                    signal: controller.signal,
                });
                clearTimeout(timeoutId);

                const data = await res.json();
                const blocked = BLOCKED_COUNTRIES.includes(data.country_code);

                sessionStorage.setItem(GEO_CACHE_KEY, String(blocked));
                setIsBlocked(blocked);
            } catch (err) {
                // Fail-open: don't block users if API fails or times out
                if (err.name !== "AbortError") {
                    console.error("Geo lookup failed:", err);
                }
                sessionStorage.setItem(GEO_CACHE_KEY, "false");
                setIsBlocked(false);
            } finally {
                setLoading(false);
            }
        };

        // Defer geo check to not block initial render
        if ("requestIdleCallback" in window) {
            requestIdleCallback(() => checkGeo(), { timeout: 2000 });
        } else {
            // Fallback: use setTimeout with small delay
            setTimeout(checkGeo, 100);
        }
    }, [initialState.loading]);

    return { loading, isBlocked };
}